import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTheme } from '../../shared/context/ThemeContext';
import {
  getComponents,
  getTenants,
  getBusinessTransactionsByTimeFilter,
  getBusinessTransactionsByDateRange,
  getDashboardErrorsDrilldown,
  getDashboardLatencyDrilldown,
  getDashboardOverview,
} from '../../shared/api/api';
import { background } from '../../shared/api/axiosConfig';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import SearchableSelect from './SearchableSelect';
import useResizableColumns from '../../shared/hooks/useResizableColumns';
import { getDisplayName } from '../../shared/auth/session';
import '../../shared/styles/resizableTable.css';
import './MainDashboardPage.css';

const COMP_TABLE_COLUMNS = [
  { key: 'name', label: 'Component', width: 200 },
  { key: 'errRate', label: 'Error Rate', width: 100 },
  { key: 'errBar', label: 'Error Bar', width: 100 },
  { key: 'err4xx', label: '4xx', width: 70 },
  { key: 'err5xx', label: '5xx', width: 70 },
  { key: 'total', label: 'Total', width: 90 },
  { key: 'red', label: 'Red', width: 70 },
  { key: 'amber', label: 'Amber', width: 80 },
  { key: 'p95', label: 'P95', width: 90 },
];

const ERROR_DRILL_COLUMNS = [
  { key: 'timestamp', label: 'Timestamp', width: 140 },
  { key: 'method', label: 'Method', width: 90 },
  { key: 'endpoint', label: 'Endpoint', width: 320 },
  { key: 'status', label: 'Status', width: 90 },
  { key: 'duration', label: 'Duration', width: 100 },
  { key: 'lat', label: 'Lat', width: 70 },
];

const LATENCY_DRILL_COLUMNS = [
  { key: 'timestamp', label: 'Timestamp', width: 140 },
  { key: 'method', label: 'Method', width: 90 },
  { key: 'endpoint', label: 'Endpoint', width: 300 },
  { key: 'status', label: 'Status', width: 90 },
  { key: 'duration', label: 'Duration', width: 100 },
  { key: 'bucket', label: 'Bucket', width: 90 },
  { key: 'error', label: 'Error', width: 70 },
];

// ── helpers ────────────────────────────────────────────────────────────────────
const TODAY = new Date().toISOString().slice(0, 10);

async function fetchAllPages(fetchFn) {
  const rows = [];
  let pageToken = null;
  let guard = 0;
  do {
    const res = await fetchFn(pageToken);
    const data = res.data;
    const page = Array.isArray(data) ? data : (data?.content || []);
    rows.push(...page);
    if (Array.isArray(data)) break;
    const isLast = data?.last === true || data?.last === 'true' || !data?.nextPageToken;
    pageToken = isLast ? null : data?.nextPageToken;
  } while (pageToken && ++guard < 20);
  return rows;
}
const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
const MC = { GET: '#3d8fff', POST: '#c41fd4', PUT: '#ffaa00', DELETE: '#ff4d6d', PATCH: '#00cc76' };

function hourOf(ts) {
  if (!ts) return -1;
  const d = new Date(ts);
  return isNaN(d) ? -1 : d.getHours();
}

function bucketByHour(transactions) {
  const buckets = Array.from({ length: 24 }, () => ({ total: 0, err4xx: 0, err5xx: 0, red: 0, amber: 0, green: 0, durSum: 0, durCount: 0 }));
  transactions.forEach(tx => {
    const h = hourOf(tx.timestamp);
    if (h < 0) return;
    buckets[h].total++;
    const s = tx.statusCode || 0;
    if (s >= 500) buckets[h].err5xx++;
    else if (s >= 400) buckets[h].err4xx++;
    const lb = (tx.latBucket || tx.lat_bucket || '').toUpperCase();
    if (lb === 'R') buckets[h].red++;
    else if (lb === 'A') buckets[h].amber++;
    else if (lb === 'G') buckets[h].green++;
    if (tx.durationMs) { buckets[h].durSum += tx.durationMs; buckets[h].durCount++; }
  });
  return buckets;
}

function statusClass(s) {
  if (s >= 500) return 'b5';
  if (s >= 400) return 'b4';
  return 'b2';
}
function latClass(lb) {
  const u = (lb || '').toUpperCase();
  if (u === 'R') return 'dur-r';
  if (u === 'A') return 'dur-a';
  return 'dur-g';
}
function latBadge(lb) {
  const u = (lb || '').toUpperCase();
  if (u === 'R') return 'badge r';
  if (u === 'A') return 'badge a';
  return 'badge g';
}

// ── SVG line/area chart ────────────────────────────────────────────────────────
function LineChart({ series, labels, height = 220, chartId = 'c' }) {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  const [animPct, setAnimPct] = useState(0);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const update = () => setW(el.getBoundingClientRect().width || 600);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el); return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setAnimPct(0);
    const dur = 900, start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / dur, 1);
      setAnimPct(1 - Math.pow(1 - p, 3));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [series]);

  const pL = 44, pR = 16, pT = 12, pB = 32;
  const cW = w - pL - pR, cH = height - pT - pB;
  const n = labels.length;
  const allVals = series.flatMap(s => s.data);
  const max = Math.max(...allVals, 1);
  const toX = i => pL + (i / (n - 1)) * cW;
  const toY = v => pT + (1 - (v * animPct) / max) * cH;
  const toYStatic = v => pT + (1 - v / max) * cH;
  const gridFs = [0, 0.25, 0.5, 0.75, 1];
  const lblIdxs = [0, 4, 8, 12, 16, 20, 23].filter(i => i < n);

  const handleMouseMove = e => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const mouseX = e.clientX - rect.left;
    let closest = 0, minDist = Infinity;
    for (let i = 0; i < n; i++) {
      const d = Math.abs(toX(i) - mouseX);
      if (d < minDist) { minDist = d; closest = i; }
    }
    setTooltip({ i: closest, x: toX(closest) });
  };

  const ttW = 130, ttH = 16 + series.length * 18;

  return (
    <div ref={ref} style={{ width: '100%', height, position: 'relative' }}
      onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        <defs>
          {series.map((s, si) => (
            <linearGradient key={si} id={`txgrad${chartId}${si}${w}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.4" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0.02" />
            </linearGradient>
          ))}
        </defs>

        {/* grid lines */}
        {gridFs.map(f => {
          const y = pT + (1 - f) * cH;
          const val = Math.round(f * max);
          return (
            <g key={f}>
              <line x1={pL} y1={y} x2={w - pR} y2={y}
                stroke="rgba(0,0,0,0.06)" strokeWidth={1} strokeDasharray={f === 0 ? 'none' : '4 3'} />
              <text x={pL - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#8b96b4" fontFamily="inherit">{val}</text>
            </g>
          );
        })}

        {/* x-axis labels */}
        {lblIdxs.map(i => (
          <text key={i} x={toX(i)} y={height - 8} textAnchor="middle" fontSize={10} fill="#8b96b4" fontFamily="inherit">
            {labels[i]}
          </text>
        ))}

        {/* area + line per series */}
        {series.map((s, si) => {
          const xs = s.data.map((_, i) => toX(i));
          const ys = s.data.map(v => toY(v));
          const path = xs.reduce((d, x, i) => {
            if (i === 0) return `M${x},${ys[i]}`;
            const cpx = (xs[i - 1] + x) / 2;
            return d + ` C${cpx},${ys[i - 1]} ${cpx},${ys[i]} ${x},${ys[i]}`;
          }, '');
          const area = `${path} L${xs[n - 1]},${pT + cH} L${xs[0]},${pT + cH} Z`;
          return (
            <g key={si}>
              <path d={area} fill={`url(#txgrad${chartId}${si}${w})`} />
              <path d={path} fill="none" stroke={s.color} strokeWidth={2.5} strokeLinejoin="round"
                style={{ filter: `drop-shadow(0 0 5px ${s.color}88)` }} />
            </g>
          );
        })}

        {/* hover crosshair + dots */}
        {tooltip && (
          <g>
            <line x1={tooltip.x} y1={pT} x2={tooltip.x} y2={pT + cH}
              stroke="rgba(0,0,0,0.15)" strokeWidth={1} strokeDasharray="4 3" />
            {series.map((s, si) => {
              const y = toYStatic(s.data[tooltip.i]);
              return <circle key={si} cx={tooltip.x} cy={y} r={4}
                fill={s.color} stroke="#fff" strokeWidth={2}
                style={{ filter: `drop-shadow(0 0 4px ${s.color})` }} />;
            })}
          </g>
        )}

        {/* tooltip box */}
        {tooltip && (() => {
          const tx = Math.min(tooltip.x + 10, w - ttW - pR);
          const ty = pT + 4;
          return (
            <g>
              <rect x={tx} y={ty} width={ttW} height={ttH} rx={6}
                fill="white" stroke="rgba(0,0,0,0.1)" strokeWidth={1}
                style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.12))' }} />
              <text x={tx + 10} y={ty + 13} fontSize={10} fill="#4e5a78" fontFamily="inherit" fontWeight="600">
                {labels[tooltip.i]}
              </text>
              {series.map((s, si) => (
                <g key={si}>
                  <circle cx={tx + 10} cy={ty + 13 + 8 + si * 18} r={4} fill={s.color} />
                  <text x={tx + 20} y={ty + 13 + 12 + si * 18} fontSize={11} fill="#0d1524" fontFamily="inherit">
                    {s.label}: <tspan fontWeight="700">{s.data[tooltip.i]}</tspan>
                  </text>
                </g>
              ))}
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ── Arc ring (tick style) ──────────────────────────────────────────────────────
function ArcRing({ pct, color, dimColor, size = 120, label, sublabel }) {
  const [disp, setDisp] = useState(0);
  useEffect(() => {
    setDisp(0);
    const dur = 900, start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / dur, 1);
      setDisp((1 - Math.pow(1 - p, 3)) * pct);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [pct]);

  const cx = size / 2, cy = size / 2, r = size / 2 - 8;
  const numTicks = 48, active = Math.round((disp / 100) * numTicks);
  const innerR = r - 8;

  return (
    <div className="txd-arc-wrap" style={{ width: size, height: size, position: 'relative' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {Array.from({ length: numTicks }, (_, i) => {
          const ang = (i / numTicks) * 360 - 90;
          const rad = ang * Math.PI / 180;
          return (
            <line key={i}
              x1={cx + innerR * Math.cos(rad)} y1={cy + innerR * Math.sin(rad)}
              x2={cx + r * Math.cos(rad)} y2={cy + r * Math.sin(rad)}
              stroke={i < active ? color : dimColor}
              strokeWidth={3} strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="txd-arc-center">
        <div className="txd-arc-val" style={{ color }}>{pct < 0.1 && pct > 0 ? pct.toFixed(2) : pct < 10 ? pct.toFixed(1) : Math.round(pct)}%</div>
        {label && <div className="txd-arc-lbl">{label}</div>}
        {sublabel && <div className="txd-arc-sub">{sublabel}</div>}
      </div>
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, cls }) {
  return (
    <div className={`txd-kpi txd-kpi--${cls}`}>
      <div className="txd-kpi-label">{label}</div>
      <div className="txd-kpi-val">{value}</div>
      {sub && <div className="txd-kpi-sub">{sub}</div>}
      <div className="txd-kpi-glow" />
    </div>
  );
}

// ── Spotlight bar chart (SVG) ─────────────────────────────────────────────────
function SpotlightLineChart({ buckets }) {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  const [H, setH] = useState(200);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setW(rect.width || 600);
      setH(rect.height || 200);
    });
    ro.observe(el); return () => ro.disconnect();
  }, []);

  const padL = 36, padR = 16, padT = 10, padB = 22;
  const cW = w - padL - padR;
  const cH = H - padT - padB;
  const n = buckets.length;

  const errData = buckets.map(b => b.err5xx + b.err4xx);
  const latData = buckets.map(b => b.red + b.amber);
  const maxVal  = Math.max(1, ...errData, ...latData);

  const toX = i => padL + (i / (n - 1)) * cW;
  const toY = v => padT + (1 - v / maxVal) * cH;

  // smooth cubic bezier path
  const makePath = data => data.reduce((d, v, i) => {
    const x = toX(i), y = toY(v);
    if (i === 0) return `M${x},${y}`;
    const px = toX(i - 1), py = toY(data[i - 1]);
    const cpx = (px + x) / 2;
    return d + ` C${cpx},${py} ${cpx},${y} ${x},${y}`;
  }, '');

  const makeArea = (data, path) => {
    const last = toX(n - 1), first = toX(0), bottom = padT + cH;
    return `${path} L${last},${bottom} L${first},${bottom} Z`;
  };

  const errPath  = makePath(errData);
  const latPath  = makePath(latData);
  const errArea  = makeArea(errData, errPath);
  const latArea  = makeArea(latData, latPath);

  // y-axis ticks — up to 4 nice round numbers
  const yTicks = [0, 0.33, 0.67, 1].map(f => Math.round(f * maxVal));
  const xLabels = [0, 4, 8, 12, 16, 20, 23];

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      <svg width={w} height={H} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="sp-err-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff4d6d" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#ff4d6d" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="sp-lat-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffaa00" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#ffaa00" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* horizontal grid lines + y-axis labels */}
        {yTicks.map((val, ti) => {
          const y = toY(val);
          return (
            <g key={ti}>
              <line x1={padL} y1={y} x2={w - padR} y2={y}
                stroke="rgba(255,255,255,.06)" strokeWidth={1}
                strokeDasharray={ti === 0 ? 'none' : '4 3'} />
              <text x={padL - 6} y={y + 4} textAnchor="end"
                fontSize={9} fill="rgba(255,255,255,.3)" fontFamily="Inter,sans-serif">
                {val}
              </text>
            </g>
          );
        })}

        {/* x-axis labels */}
        {xLabels.map(h => (
          <text key={h} x={toX(h)} y={H - 5} textAnchor="middle"
            fontSize={9} fill="rgba(255,255,255,.28)" fontFamily="Inter,sans-serif">
            {String(h).padStart(2, '0')}:00
          </text>
        ))}

        {/* latency area + line */}
        <path d={latArea} fill="url(#sp-lat-grad)" />
        <path d={latPath} fill="none" stroke="#ffaa00" strokeWidth={2}
          strokeLinejoin="round"
          style={{ filter: 'drop-shadow(0 0 4px rgba(255,170,0,.4))' }} />

        {/* errors area + line */}
        <path d={errArea} fill="url(#sp-err-grad)" />
        <path d={errPath} fill="none" stroke="#ff4d6d" strokeWidth={2}
          strokeLinejoin="round"
          style={{ filter: 'drop-shadow(0 0 4px rgba(255,77,109,.4))' }} />
      </svg>
    </div>
  );
}

// ── Spotlight view — Design 2 "Edge Panels" ───────────────────────────────────
function SpotlightView({ tenants, errRows, latRows, onExit, theme }) {
  const [idx, setIdx]       = useState(0);
  const [paused, setPaused] = useState(false);
  const [pct, setPct]       = useState(0);
  const [clock, setClock]   = useState('');
  const pctRef              = useRef(0);
  const barRefs             = useRef({});

  const total = tenants.length;
  const t     = tenants[idx] || null;

  const goTo = useCallback((i) => { setIdx(i); pctRef.current = 0; setPct(0); }, []);
  const next = useCallback(() => goTo((idx + 1) % total), [idx, total, goTo]);
  const prev = useCallback(() => goTo((idx - 1 + total) % total), [idx, total, goTo]);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const h = String(now.getHours()).padStart(2, '0');
      const m = String(now.getMinutes()).padStart(2, '0');
      const s = String(now.getSeconds()).padStart(2, '0');
      setClock(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (paused) return;
    const TICK = 80, DURATION = 8000;
    const id = setInterval(() => {
      pctRef.current += (TICK / DURATION) * 100;
      if (pctRef.current >= 100) { pctRef.current = 0; next(); }
      setPct(Math.min(pctRef.current, 100));
    }, TICK);
    return () => clearInterval(id);
  }, [paused, next]);

  useEffect(() => {
    const h = e => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') onExit();
      else if (e.key === 'p' || e.key === 'P') setPaused(p => !p);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [next, prev, onExit]);

  // animate component bar fills after render
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      Object.entries(barRefs.current).forEach(([key, el]) => {
        if (el) el.style.width = el.dataset.target;
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [idx]);

  // compute per-tenant 24h buckets — must be before any early return to satisfy Rules of Hooks
  const tenantHourlyBuckets = useMemo(() => {
    const tid = t?.tenantId;
    if (!tid) return Array.from({ length: 24 }, () => ({ err4xx: 0, err5xx: 0, red: 0, amber: 0 }));
    const tenantErrRows = (errRows || []).filter(r => r.tenantId === tid);
    const tenantLatRows = (latRows || []).filter(r => r.tenantId === tid);
    const buckets = Array.from({ length: 24 }, () => ({ err4xx: 0, err5xx: 0, red: 0, amber: 0 }));
    tenantErrRows.forEach(r => {
      const h = hourOf(r.timestamp); if (h < 0) return;
      const s = r.statusCode || 0;
      if (s >= 500) buckets[h].err5xx++; else if (s >= 400) buckets[h].err4xx++;
    });
    tenantLatRows.forEach(r => {
      const h = hourOf(r.timestamp); if (h < 0) return;
      const lb = (r.latBucket || '').toUpperCase();
      if (lb === 'R') buckets[h].red++; else if (lb === 'A') buckets[h].amber++;
    });
    return buckets;
  }, [t?.tenantId, errRows, latRows]);

  if (!t) return null;

  const comps = t.components || [];

  const totalErrors = comps.reduce((s, c) => s + (c.errorCount || 0), 0);
  const totalRed    = comps.reduce((s, c) => s + (c.redCount   || 0), 0);
  const totalAmber  = comps.reduce((s, c) => s + (c.amberCount || 0), 0);
  const healthy     = comps.length === 0 || (totalErrors === 0 && totalRed === 0 && totalAmber === 0);
  const status      = healthy ? 'healthy' : (totalErrors > 0 || totalRed > 0) ? 'issues' : 'warning';

  const tenantStatus = t2 => {
    if (!t2.components || t2.components.length === 0) return 'healthy';
    const hasErr = t2.components.some(c => c.errorCount > 0 || c.redCount > 0);
    const hasAmb = t2.components.some(c => c.amberCount > 0);
    return hasErr ? 'issues' : hasAmb ? 'warning' : 'healthy';
  };
  const dotColor = { healthy: 'rgba(0,230,118,.7)', warning: '#ffaa00', issues: '#ff2d55' };

  const rankedComps = [...comps]
    .map(c => ({ ...c, total: c.errorCount + c.redCount + c.amberCount }))
    .sort((a, b) => b.total - a.total);
  const maxCompTotal = Math.max(1, ...rankedComps.map(c => c.total));
  const top3 = rankedComps.slice(0, 3);

  const statusCardCls = status === 'issues' ? 'sp-info-card--status-crit' : status === 'warning' ? 'sp-info-card--status-warn' : 'sp-info-card--status-ok';
  const statusLabel   = status === 'issues' ? 'CRITICAL' : status === 'warning' ? 'DEGRADED' : 'HEALTHY';

  const tenantNameCls = status === 'healthy' ? 'sp-tenant-name sp-tenant-name--healthy' : status === 'warning' ? 'sp-tenant-name sp-tenant-name--warning' : 'sp-tenant-name';
  const badgeCls      = status === 'healthy' ? 'sp-issues-badge sp-issues-badge--healthy' : status === 'warning' ? 'sp-issues-badge sp-issues-badge--warning' : 'sp-issues-badge';
  const badgeText     = status === 'healthy' ? 'All Systems Clear' : status === 'warning' ? '◑ Degraded Performance' : 'Issues Detected';

  return (
    <div className={`sp-shell${theme === 'light' ? ' sp-light' : ''}`}>
      <div className="sp-scan-line" />

      <div className="sp-app">
        {/* TOP BAR */}
        <header className="sp-topbar">
          <div className="sp-topbar-left">
            <div className="sp-live-badge">
              <div className="sp-live-dot" />
              <span className="sp-live-lbl">Live</span>
            </div>
            <span className="sp-all-tenants-lbl">{idx + 1} / {total} Tenants</span>
          </div>

          <div className="sp-topbar-center">
            <div className="sp-tenant-nav-dots">
              {tenants.map((tn, i) => {
                const st = tenantStatus(tn);
                return (
                  <button key={i}
                    className={`sp-dot${i === idx ? ' sp-dot--active' : ''}`}
                    style={{ background: i === idx ? '#fff' : dotColor[st], boxShadow: i !== idx ? `0 0 5px ${dotColor[st]}` : undefined }}
                    onClick={() => { goTo(i); setPaused(true); }}
                    title={tn.tenantId}
                  />
                );
              })}
            </div>
          </div>

          <div className="sp-topbar-right">
            <span className="sp-clock">{clock}</span>
            <span className="sp-utc-tag">UTC</span>
            <button className="sp-ctrl-btn" onClick={() => setPaused(p => !p)}>{paused ? '▶' : '⏸'}</button>
            <button className="sp-ctrl-btn" onClick={prev}>‹</button>
            <button className="sp-ctrl-btn" onClick={next}>›</button>
            <button className="sp-exit-btn" onClick={onExit}>✕ Exit</button>
          </div>
        </header>
        <div className="sp-progress-track"><div className="sp-progress-fill" style={{ width: `${pct}%` }} /></div>

        {/* THREE COLUMNS */}
        <div className="sp-columns" key={idx}>

          {/* LEFT: METRIC RAIL */}
          <aside className="sp-metric-rail">
            <div className="sp-metric-card sp-metric-card--crit">
              <div className="sp-corner-tl" /><div className="sp-corner-br" />
              <div className="sp-metric-num">{totalErrors}</div>
              <div className="sp-metric-lbl">Errors</div>
              <div className="sp-metric-sub">HTTP 4xx + 5xx</div>
            </div>
            <div className="sp-metric-card sp-metric-card--warn">
              <div className="sp-corner-tl" /><div className="sp-corner-br" />
              <div className="sp-metric-num">{totalRed}</div>
              <div className="sp-metric-lbl">Critical</div>
              <div className="sp-metric-sub">&gt;10s response</div>
            </div>
            <div className="sp-metric-card sp-metric-card--ok">
              <div className="sp-corner-tl" /><div className="sp-corner-br" />
              <div className="sp-metric-num">{totalAmber}</div>
              <div className="sp-metric-lbl">Degraded</div>
              <div className="sp-metric-sub">5–10s response</div>
            </div>
          </aside>

          {/* CENTER: HERO + SPARKLINE */}
          <main className="sp-center">
            <div className="sp-hero-header">
              <div className="sp-hero-eyebrow">Tenant · {idx + 1} of {total}</div>
              <div className={tenantNameCls}>{t.tenantId}</div>
              <div className={badgeCls}>
                <span>{badgeText}</span>
                {status !== 'healthy' && <span>⚠</span>}
              </div>
            </div>

            <div className="sp-chart-panel">
              <div className="sp-chart-title-row">
                {/* Not actually a rolling 24h window — this and the three tiles
                    beside it come from getDashboardErrorsDrilldown/
                    getDashboardLatencyDrilldown(currentDate, currentDate), a
                    single calendar day since 00:00 UTC. Label says so instead
                    of overstating how much data is behind it, especially
                    early in the day. */}
                <span className="sp-chart-title">Errors &amp; Slow Calls — Today</span>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.6rem', color: 'rgba(255,255,255,.45)' }}>
                    <span style={{ width: 20, height: 2, borderRadius: 2, background: '#ff4d6d', display: 'inline-block' }} />HTTP Errors
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.6rem', color: 'rgba(255,255,255,.45)' }}>
                    <span style={{ width: 20, height: 2, borderRadius: 2, background: '#ffaa00', display: 'inline-block' }} />Slow Calls
                  </span>
                </div>
              </div>
              <div className="sp-chart-area">
                <SpotlightLineChart buckets={tenantHourlyBuckets} />
              </div>
            </div>
          </main>

          {/* RIGHT: COMPONENT BARS + INFO CARDS */}
          <aside className="sp-right">
            <div className="sp-panel sp-comps-panel">
              <div className="sp-panel-title">Top Affected Components</div>
              {top3.length === 0 ? (
                <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.3)', paddingTop: 8 }}>No components</div>
              ) : top3.map((c, ci) => {
                const cs = (c.errorCount > 0 || c.redCount > 0) ? 'crit' : c.amberCount > 0 ? 'warn' : 'ok';
                const widthPct = `${Math.round((c.total / maxCompTotal) * 100)}%`;
                return (
                  <div key={c.componentName} className="sp-comp-row">
                    <div className="sp-comp-header">
                      <span className="sp-comp-name">{c.componentName}</span>
                      <span className={`sp-comp-val sp-comp-val--${cs}`}>{c.total}</span>
                    </div>
                    <div className="sp-comp-bar-track">
                      <div
                        className={`sp-comp-bar-fill sp-comp-bar-fill--${cs}`}
                        data-target={widthPct}
                        ref={el => { barRefs.current[`${idx}-${ci}`] = el; }}
                        style={{ width: 0 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sp-info-cards">
              <div className="sp-info-card sp-info-card--navigator">
                <div className="sp-ic-tl" /><div className="sp-ic-br" />
                <span className="sp-info-card-lbl">Components</span>
                <span className="sp-info-card-val">{t.components.length}</span>
              </div>
              <div className={`sp-info-card ${statusCardCls}`}>
                <div className="sp-ic-tl" /><div className="sp-ic-br" />
                <span className="sp-info-card-lbl">Status</span>
                <span className="sp-info-card-val">{statusLabel}</span>
              </div>
            </div>
          </aside>

        </div>

        {/* FOOTER */}
        <footer className="sp-footer">
          <span className="sp-footer-text">Observability Platform</span>
          <div className="sp-footer-dot" />
          <span className="sp-footer-text">Command Quality</span>
          <div className="sp-footer-dot" />
          <span className="sp-bottom-hint">P pause · ← → navigate · Esc exit</span>
        </footer>
      </div>
    </div>
  );
}

// The custom TenantDropdown that used to live here was replaced by the shared
// SearchableSelect in TSB-4302 — same control as the Analytics filter bar, with
// a search box the original lacked.

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MainDashboardPage() {
  const { theme } = useTheme();

  const compTable = useResizableColumns(COMP_TABLE_COLUMNS);
  const errDrillTable = useResizableColumns(ERROR_DRILL_COLUMNS);
  const latDrillTable = useResizableColumns(LATENCY_DRILL_COLUMNS);

  // selectors
  const [tenantsList, setTenantsList] = useState([]);
  const [componentsList, setComponentsList] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('__all__');
  const [selectedComponent, setSelectedComponent] = useState('');
  const timeFilter = '24h';
  const [clock, setClock] = useState('');

  // data
  const [transactions, setTransactions] = useState([]);
  const [overviewTenants, setOverviewTenants] = useState([]);
  const [allTenantsErrSplit, setAllTenantsErrSplit] = useState({ err4xx: 0, err5xx: 0 });
  const [allTenantsErrRows, setAllTenantsErrRows] = useState([]);
  const [allTenantsLatRows, setAllTenantsLatRows] = useState([]);
  const [singleTenantErrRows, setSingleTenantErrRows] = useState([]);
  const [singleTenantLatRows, setSingleTenantLatRows] = useState([]);
  // Same principle as `loading` above, applied to switching tenants instead of
  // the first load: while this is true, singleTenantErrRows/LatRows are stale
  // or empty and must not be read as "confirmed zero" — see allHealthy and the
  // single-tenant KPI tiles below, both of which check it before trusting 0.
  const [singleTenantDrillLoading, setSingleTenantDrillLoading] = useState(false);
  const [errDrillRows, setErrDrillRows] = useState([]);
  const [latDrillRows, setLatDrillRows] = useState([]);
  // Starts true, matching DashboardPageV2. Starting false meant the body — which
  // is gated on !loading — rendered a full set of zeroed KPI cards before a
  // single request had been made, then unmounted them all when the transaction
  // effect finally fired and flipped loading to true. The user saw zeros, then a
  // spinner, then real data. A KPI reading "Total Errors: 0" is a statement, and
  // for that first render it was a false one.
  const [loading, setLoading] = useState(true);
  // Whether the tenant/component/overview fetch has finished, successfully or
  // not. The transaction effect used to gate on `componentsList.length > 0`,
  // which silently means "if there are no components, never stop loading" — safe
  // only while loading started false. This flag lets the effect run once the
  // selectors have settled and hands the empty case to fetchTransactions, which
  // already clears loading for it.
  const [selectorsReady, setSelectorsReady] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [error, setError] = useState('');
  const isInitialLoad = useRef(true);

  // view: 'all' or 'comp'
  const [view, setView] = useState('all');
  const [activeTab, setActiveTab] = useState('errors');
  const [spotlightMode, setSpotlightMode] = useState(false);

  // clock
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('en-ZA'));
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, []);

  // load tenants + components + overview — extracted so it can be called on mount and every 60s
  /**
   * @param isBackground true for the five-minute auto-refresh below.
   *
   * A refresh nobody asked for must not be able to end the session: a 401 on one
   * of these would otherwise sign the person out of a page they were reading.
   * The flag rides along on every request this fans out to — see the 401 rule in
   * shared/api/axiosConfig.
   */
  const fetchOverviewData = useCallback((isBackground = false) => {
    const currentDate = new Date().toISOString().slice(0, 10);
    const opts = isBackground ? background() : undefined;
    Promise.allSettled([getTenants(opts), getComponents(opts), getDashboardOverview(currentDate, currentDate, opts)]).then(([tRes, cRes, ovRes]) => {
      if (tRes.status === 'fulfilled') {
        const d = tRes.value.data;
        const list = Array.isArray(d) ? d : d?.tenants || d?.content || [];
        setTenantsList(list);
      }
      if (cRes.status === 'fulfilled') {
        const d = cRes.value.data;
        setComponentsList(Array.isArray(d) ? d : []);
      }
      if (ovRes.status === 'fulfilled') {
        const ovd = ovRes.value.data;
        const tenants = Array.isArray(ovd) ? ovd : (ovd?.tenants || ovd?.data || []);
        setOverviewTenants(tenants);
        // Drilldown pairs come from the overview itself, not tenants x components.
        // The cross-product was 19 tenants x 4 components = 76 pairs, each costing an
        // errors AND a latency call, ~152 of the ~170 requests this page made per
        // refresh. But both drilldown endpoints only return error and slow
        // transactions, and the overview already lists exactly which components hold
        // any — a component absent from t.components has nothing to return, so those
        // calls came back empty. Same data on screen, a fraction of the requests.
        const pairs = tenants.flatMap(t =>
          (t.components || []).map(c => ({ tid: t.tenantId, c: c.componentName }))
        );
        if (pairs.length) {
          Promise.allSettled([
            Promise.allSettled(pairs.map(({ tid, c }) =>
              fetchAllPages(token => getDashboardErrorsDrilldown(tid, c, currentDate, currentDate, 500, token, opts))
                .then(rows => rows.map(r => r.tenantId ? r : { ...r, tenantId: tid }))
            )),
            Promise.allSettled(pairs.map(({ tid, c }) =>
              fetchAllPages(token => getDashboardLatencyDrilldown(tid, c, currentDate, currentDate, null, 500, token, opts))
                .then(rows => rows.map(r => r.tenantId ? r : { ...r, tenantId: tid }))
            )),
          ]).then(([errBatch, latBatch]) => {
            const parseRows = results => results.flatMap(r =>
              r.status === 'fulfilled' ? r.value : []
            );
            const errRows = parseRows(errBatch.value || []);
            const latRows = parseRows(latBatch.value || []);
            let err4xx = 0, err5xx = 0;
            errRows.forEach(row => {
              const s = row.statusCode || 0;
              if (s >= 500) err5xx++;
              else if (s >= 400) err4xx++;
            });
            setAllTenantsErrSplit({ err4xx, err5xx });
            setAllTenantsErrRows(errRows);
            setAllTenantsLatRows(latRows);
          });
        }
      }
      // Set regardless of which requests succeeded: a failed selector fetch must
      // still release the transaction effect, or a total outage leaves the page
      // spinning forever instead of showing an empty dashboard.
      setSelectorsReady(true);
    });
  }, []);

  useEffect(() => {
    fetchOverviewData();
    // 5 minutes, not 60s: every query on this page is scoped to a single calendar
    // day (fromDate = toDate = today), so a per-minute refresh asks for a daily
    // total 60 times an hour and cannot show anything new for the cost.
    const t = setInterval(() => {
      isInitialLoad.current = false;
      fetchOverviewData(true);
    }, 300000);
    return () => clearInterval(t);
  }, [fetchOverviewData]);

  const resolveTenantId = useCallback((t) =>
    typeof t === 'string' ? t : t?.tenantId || t?.id || t?.name, []);

  // fetch transactions whenever tenant/timeFilter changes
  const fetchTransactions = useCallback(async (tenant, tf, comp) => {
    if (!tenant) return;
    if (isInitialLoad.current) setLoading(true);
    setError('');
    try {
      const comps = comp ? [comp] : componentsList;
      if (!comps.length) {
        setTransactions([]);
        setLoading(false);
        return;
      }
      let merged;
      if (tenant === '__all__') {
        const tenantIds = tenantsList.map(t => (typeof t === 'string' ? t : t?.tenantId || t?.id));
        const pairs = tenantIds.flatMap(tid => comps.map(c => ({ tid, c })));
        const results = await Promise.allSettled(
          pairs.map(({ tid, c }) => getBusinessTransactionsByTimeFilter(tf, c, tid))
        );
        merged = results.flatMap((r, i) => {
          if (r.status !== 'fulfilled') return [];
          const d = r.value.data;
          const rows = Array.isArray(d) ? d : d?.transactions || d?.content || [];
          return rows.map(tx => ({ ...tx, tenantId: tx.tenantId ?? pairs[i].tid }));
        });
      } else {
        const pairs = comps.map(c => ({ tid: tenant, c }));
        const results = await Promise.allSettled(
          pairs.map(({ tid, c }) => getBusinessTransactionsByTimeFilter(tf, c, tid))
        );
        merged = results.flatMap((r, i) => {
          if (r.status !== 'fulfilled') return [];
          const d = r.value.data;
          const rows = Array.isArray(d) ? d : d?.transactions || d?.content || [];
          return rows.map(tx => ({ ...tx, tenantId: tx.tenantId ?? pairs[i].tid }));
        });
      }
      setTransactions(merged);
    } catch {
      setError('Failed to load transactions.');
    } finally {
      setLoading(false);
      isInitialLoad.current = false;
    }
  }, [componentsList, tenantsList]);

  // fetch drilldown rows for single-component view
  const fetchDrilldown = useCallback(async (tenant, comp) => {
    if (!tenant || !comp) return;
    setDrillLoading(true);
    try {
      const [eRes, lRes] = await Promise.allSettled([
        getDashboardErrorsDrilldown(tenant, comp, TODAY, TODAY, 100),
        getDashboardLatencyDrilldown(tenant, comp, TODAY, TODAY, null, 100),
      ]);
      if (eRes.status === 'fulfilled') {
        const d = eRes.value.data;
        setErrDrillRows(Array.isArray(d) ? d : d?.items || d?.content || []);
      }
      if (lRes.status === 'fulfilled') {
        const d = lRes.value.data;
        setLatDrillRows(Array.isArray(d) ? d : d?.items || d?.content || []);
      }
    } catch { /* leave existing */ }
    finally { setDrillLoading(false); }
  }, []);

  useEffect(() => {
    if (!selectorsReady || !selectedTenant) return;
    // No components is a legitimate outcome, not a reason to wait: fetchTransactions
    // clears loading and empties the list for it.
    fetchTransactions(selectedTenant, timeFilter, selectedComponent || null);
  }, [selectorsReady, selectedTenant, timeFilter, selectedComponent, componentsList, tenantsList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Which tenant the in-flight (or most recently started) drilldown request is
  // for. Lets a response recognise it's stale — e.g. switching CMDQSIT ->
  // DFA21C94 -> CMDQSIT fires three requests that can resolve out of order;
  // without this the middle one (DFA21C94) could land last and overwrite
  // CMDQSIT's already-correct numbers with the wrong tenant's data.
  const singleTenantRequestRef = useRef(null);

  // for single-tenant mode, fetch drilldowns (errors + latency) across all
  // components. Does NOT clear singleTenantErrRows/LatRows before fetching —
  // see the comment on singleTenantDrillLoading above. Same "leave existing"
  // shape as fetchDrilldown below; if a third fetch in this file ever needs
  // this pattern, pull it out into a shared hook instead of copying it again.
  const fetchSingleTenantDrilldowns = useCallback((tid, compNames) => {
    singleTenantRequestRef.current = tid;
    setSingleTenantDrillLoading(true);
    const currentDate = new Date().toISOString().slice(0, 10);
    const pairs = compNames.map(c => ({ tid, c }));
    Promise.allSettled([
      Promise.allSettled(pairs.map(({ tid, c }) =>
        fetchAllPages(token => getDashboardErrorsDrilldown(tid, c, currentDate, currentDate, 500, token))
      )),
      Promise.allSettled(pairs.map(({ tid, c }) =>
        fetchAllPages(token => getDashboardLatencyDrilldown(tid, c, currentDate, currentDate, null, 500, token))
      )),
    ]).then(([errBatch, latBatch]) => {
      if (singleTenantRequestRef.current !== tid) return; // superseded by a newer request
      const parseRows = results => results.flatMap(r => r.status === 'fulfilled' ? r.value : []);
      setSingleTenantErrRows(parseRows(errBatch.value || []));
      setSingleTenantLatRows(parseRows(latBatch.value || []));
      setSingleTenantDrillLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedTenant && selectedTenant !== '__all__' && componentsList.length > 0) {
      const compNames = componentsList.map(c => typeof c === 'string' ? c : c?.name || c?.componentName);
      fetchSingleTenantDrilldowns(selectedTenant, compNames);
    }
  }, [selectedTenant, componentsList, fetchSingleTenantDrilldowns]);

  // when component selected → switch to component view + load drilldown
  const handleSelectComponent = useCallback((compName) => {
    setSelectedComponent(compName);
    setView('comp');
    setActiveTab('errors');
    fetchDrilldown(selectedTenant, compName);
  }, [selectedTenant, fetchDrilldown]);

  const handleBackToAll = useCallback(() => {
    setSelectedComponent('');
    setView('all');
  }, []);

  // ── derived data ─────────────────────────────────────────────────────────────
  const displayedTx = useMemo(() => {
    if (!selectedComponent || view === 'all') return transactions;
    return transactions.filter(tx => tx.componentName === selectedComponent);
  }, [transactions, selectedComponent, view]);

  const hourlyBuckets = useMemo(() => bucketByHour(displayedTx), [displayedTx]);

  const kpisFromTx = useMemo(() => {
    // single-component view uses filtered transactions; single-tenant all-components uses drilldown rows
    const useDrilldown = selectedTenant !== '__all__' && view !== 'comp';
    const errRows = useDrilldown ? singleTenantErrRows : displayedTx.filter(t => t.statusCode >= 400);
    const latRows = useDrilldown ? singleTenantLatRows : displayedTx;
    const txRows  = useDrilldown ? transactions : displayedTx;
    const total   = txRows.length;
    const err4xx  = (useDrilldown ? errRows : displayedTx).filter(t => (t.statusCode || 0) >= 400 && (t.statusCode || 0) < 500).length;
    const err5xx  = (useDrilldown ? errRows : displayedTx).filter(t => (t.statusCode || 0) >= 500).length;
    const errTotal = err4xx + err5xx;
    const errRate  = total > 0 ? ((errTotal / total) * 100).toFixed(1) : '0.0';
    const red   = latRows.filter(t => (t.latBucket || '').toUpperCase() === 'R').length;
    const amber = latRows.filter(t => (t.latBucket || '').toUpperCase() === 'A').length;
    const green = latRows.filter(t => (t.latBucket || '').toUpperCase() === 'G').length;
    const slowPct = total > 0 ? (((red + amber) / total) * 100).toFixed(1) : '0.0';
    const durations = txRows.map(t => t.durationMs).filter(Boolean);
    const avgDur = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
    return { total, errRate, errTotal, err4xx, err5xx, red, amber, green, slowPct, avgDur };
  }, [displayedTx, transactions, singleTenantErrRows, singleTenantLatRows, selectedTenant, view]);

  // when all-tenants mode: errors + latency counts from overview (consistent with heatmap),
  // green latency from transactions (overview doesn't track it)
  const kpisFromOverview = useMemo(() => {
    const allComps = overviewTenants.flatMap(t => t.components);
    const errTotal = allComps.reduce((s, c) => s + c.errorCount, 0);
    const red      = allComps.reduce((s, c) => s + c.redCount, 0);
    const amber    = allComps.reduce((s, c) => s + c.amberCount, 0);
    const green      = kpisFromTx.green;
    const txTotal    = kpisFromTx.total;
    const issueTotal = allComps.reduce((s, c) => s + c.errorCount + c.redCount + c.amberCount, 0);
    const slowPct    = txTotal > 0 ? (((red + amber) / txTotal) * 100).toFixed(1) : '0.0';
    const errRate    = txTotal > 0 ? ((errTotal / txTotal) * 100).toFixed(1) : '0.0';
    const { err4xx, err5xx } = allTenantsErrSplit;
    return { total: txTotal, errRate, errTotal, err4xx, err5xx, red, amber, green, slowPct, avgDur: kpisFromTx.avgDur };
  }, [overviewTenants, allTenantsErrSplit, kpisFromTx]);

  const kpis = selectedTenant === '__all__' ? kpisFromOverview : kpisFromTx;

  // per-component summary (for all-components table)
  const compSummary = useMemo(() => {
    const map = {};
    transactions.forEach(tx => {
      const c = tx.componentName || 'unknown';
      if (!map[c]) map[c] = { name: c, total: 0, err4xx: 0, err5xx: 0, red: 0, amber: 0, green: 0, durations: [] };
      map[c].total++;
      const s = tx.statusCode || 0;
      if (s >= 500) map[c].err5xx++;
      else if (s >= 400) map[c].err4xx++;
      const lb = (tx.latBucket || '').toUpperCase();
      if (lb === 'R') map[c].red++;
      else if (lb === 'A') map[c].amber++;
      else if (lb === 'G') map[c].green++;
      if (tx.durationMs) map[c].durations.push(tx.durationMs);
    });
    return Object.values(map).map(c => {
      const errTotal = c.err4xx + c.err5xx;
      const errRate = c.total > 0 ? ((errTotal / c.total) * 100).toFixed(1) : '0.0';
      const sorted = [...c.durations].sort((a, b) => a - b);
      const p95 = sorted.length ? sorted[Math.floor(sorted.length * 0.95)] : 0;
      const color = parseFloat(errRate) > 5 ? '#ff4d6d' : parseFloat(errRate) > 2 ? '#ffaa00' : '#00cc76';
      return { ...c, errTotal, errRate, p95, color };
    }).sort((a, b) => parseFloat(b.errRate) - parseFloat(a.errRate));
  }, [transactions]);

  const drillErrBuckets = useMemo(() => {
    const rows = selectedTenant === '__all__' ? allTenantsErrRows : singleTenantErrRows;
    const buckets = Array.from({ length: 24 }, () => ({ err4xx: 0, err5xx: 0 }));
    rows.forEach(row => {
      const h = hourOf(row.timestamp);
      if (h < 0) return;
      const s = row.statusCode || 0;
      if (s >= 500) buckets[h].err5xx++;
      else if (s >= 400) buckets[h].err4xx++;
    });
    return buckets;
  }, [allTenantsErrRows, singleTenantErrRows, selectedTenant]);

  const drillLatBuckets = useMemo(() => {
    const rows = selectedTenant === '__all__' ? allTenantsLatRows : singleTenantLatRows;
    const buckets = Array.from({ length: 24 }, () => ({ red: 0, amber: 0, green: 0 }));
    rows.forEach(row => {
      const h = hourOf(row.timestamp);
      if (h < 0) return;
      const lb = (row.latBucket || '').toUpperCase();
      if (lb === 'R') buckets[h].red++;
      else if (lb === 'A') buckets[h].amber++;
      else if (lb === 'G') buckets[h].green++;
    });
    return buckets;
  }, [allTenantsLatRows, singleTenantLatRows, selectedTenant]);

  const errChartSeries = useMemo(() => {
    // single-component view still uses transaction-based hourlyBuckets (drilldown is per component anyway)
    const b = view === 'comp' ? hourlyBuckets : drillErrBuckets;
    return [
      { color: '#ff4d6d', data: b.map(x => x.err5xx), label: '5xx' },
      { color: '#ffaa00', data: b.map(x => x.err4xx), label: '4xx' },
    ];
  }, [drillErrBuckets, hourlyBuckets, view]);

  const latChartSeries = useMemo(() => {
    const b = view === 'comp' ? hourlyBuckets : drillLatBuckets;
    return [
      { color: '#ffaa00', data: b.map(x => x.amber), label: 'Amber' },
      { color: '#ff4d6d', data: b.map(x => x.red),   label: 'Red'   },
    ];
  }, [drillLatBuckets, hourlyBuckets, view]);

  const avgDurSeries = useMemo(() => [{
    color: '#3d8fff',
    label: 'Avg Duration (ms)',
    data: hourlyBuckets.map(b => b.durCount > 0 ? Math.round(b.durSum / b.durCount) : 0),
  }], [hourlyBuckets]);

  // tenant × component heatmap — built from overview endpoint (same as DashboardOverviewPage)
  const tenantHeatmap = useMemo(() => {
    const fromOverview = [...new Set(overviewTenants.flatMap(t => t.components.map(c => c.componentName)))];
    const compNames = fromOverview.length > 0
      ? fromOverview.sort()
      : componentsList.map(c => typeof c === 'string' ? c : c?.name || c?.componentName).filter(Boolean).sort();
    return { overviewTenants, compNames };
  }, [overviewTenants, componentsList]);

  // worst component for KPI card
  const worstComp = compSummary[0];

  const tenantLabel = selectedTenant === '__all__' ? 'All Tenants' : selectedTenant || 'All Tenants';

  const username  = getDisplayName() || 'Developer';
  const firstName = username.split(' ')[0];
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // singleTenantDrillLoading only ever matters when a specific tenant is
  // selected (kpisFromOverview, used for '__all__', never depends on it) —
  // included unconditionally is still correct, since it's false whenever
  // '__all__' is selected.
  const allHealthy = !loading && !singleTenantDrillLoading && kpis.errTotal === 0 && kpis.red === 0;

  return (
    <div className={`txd-shell${theme === 'light' ? ' txd-light' : ''}`}>

      {/* Spotlight overlay */}
      {spotlightMode && !loading && overviewTenants.length > 0 && (
        <SpotlightView
          tenants={overviewTenants}
          errRows={allTenantsErrRows}
          latRows={allTenantsLatRows}
          onExit={() => setSpotlightMode(false)}
          theme={theme}
        />
      )}

      {/* TOPBAR */}
      <div className="txd-topbar">
        <div className="txd-topbar-title">{greeting}, <span style={{ color: 'var(--txd-cq2)' }}>{firstName}</span></div>
        <div className="txd-topbar-sep" />
        <div className="txd-topbar-sub">Main Dashboard</div>
        <div className="txd-topbar-right">
          {/* Tenant — searchable, same control as the Analytics filter bar
              (TSB-4302). This page uses '__all__' rather than '' for "no
              filter", so the value is mapped either side. */}
          <SearchableSelect
            value={selectedTenant === '__all__' ? '' : selectedTenant}
            options={tenantsList.map(t => {
              const id = resolveTenantId(t);
              return { id, label: id };
            })}
            onChange={id => { setSelectedTenant(id || '__all__'); setView('all'); setSelectedComponent(''); }}
            allLabel="All Tenants"
            placeholder="Search tenants…"
            triggerClassName="txd-dropdown-btn"
          />

          <div className="txd-live-badge"><div className="txd-live-dot" />Live · 60s</div>
          <button className="txd-spotlight-btn" onClick={() => setSpotlightMode(true)}>⊞ Spotlight</button>
          <div className="txd-clock">{clock}</div>
        </div>
      </div>

      {/* BODY */}
      <div className="txd-body">
        {loading && <LoadingSpinner message="Loading transactions…" />}
        {error && <div className="txd-error">{error}</div>}
        {!loading && (
          <>
            {/* ── ALL COMPONENTS VIEW ────────────────────────────────── */}
            {view === 'all' && (
              <>
                {/* ── Overview ── */}
                <div className="v2-section-divider">
                  <span className="v2-section-label">Overview</span>
                  <div className="v2-section-line" />
                </div>

                {/* All Systems Healthy banner */}
                {allHealthy && (
                  <div className="txd-healthy-banner">
                    <div className="txd-healthy-icon">✓</div>
                    <div className="txd-healthy-text">
                      <div className="txd-healthy-title">All Systems Healthy</div>
                      <div className="txd-healthy-sub">No errors or critical latency detected · {tenantLabel}</div>
                    </div>
                    <div className="txd-healthy-right">
                      <div className="txd-healthy-dot" />
                      <span className="txd-healthy-lbl">Nominal</span>
                    </div>
                  </div>
                )}

                {/* KPI strip */}
                <div className="txd-kpi-row">
                  {selectedTenant === '__all__' ? (<>
                    <KpiCard label="Total Errors"   value={kpis.errTotal}               sub={`across all tenants · today`}              cls={kpis.errTotal === 0 ? 'grn' : 'red'} />
                    <KpiCard label="Red (>10s)"     value={kpis.red.toLocaleString()}   sub="critical latency"                          cls={kpis.red === 0 ? 'grn' : 'red'} />
                    <KpiCard label="Amber (5–10s)"  value={kpis.amber.toLocaleString()} sub="degraded latency"                          cls={kpis.amber === 0 ? 'grn' : 'amb'} />
                    <KpiCard label="Tenants"        value={overviewTenants.length}      sub={`${overviewTenants.filter(t => t.components.some(c => c.errorCount > 0 || c.redCount > 0)).length} with issues`} cls="cq" />
                  </>) : (<>
                    {/* singleTenantDrillLoading: mid-fetch, errTotal reads 0 because
                        nothing has answered yet, not because it's confirmed zero — so
                        it shows a neutral "…" rather than a green 0 that reads as an
                        answer already given. */}
                    <KpiCard label="Error Rate"      value={singleTenantDrillLoading ? '…' : `${kpis.errRate}%`}  sub={singleTenantDrillLoading ? 'loading…' : `${kpis.errTotal} errors · 4xx + 5xx`} cls={singleTenantDrillLoading ? 'cq' : kpis.errTotal === 0 ? 'grn' : 'red'} />
                    <KpiCard label="Total Errors"    value={singleTenantDrillLoading ? '…' : kpis.errTotal}        sub={singleTenantDrillLoading ? 'loading…' : `${kpis.err4xx} client · ${kpis.err5xx} server`} cls={singleTenantDrillLoading ? 'cq' : kpis.errTotal === 0 ? 'grn' : 'red'} />
                    <KpiCard label="Throughput"      value={kpis.total.toLocaleString()} sub="last 24h" cls="cq" />
                    <KpiCard label="Needs Attention"
                      value={worstComp ? `${worstComp.errRate}%` : '—'}
                      sub={worstComp?.name || 'No data'}
                      cls={!worstComp || parseFloat(worstComp.errRate) === 0 ? 'grn' : 'red'} />
                  </>)}
                </div>

                {/* ── Trends ── */}
                <div className="v2-section-divider">
                  <span className="v2-section-label">Trends</span>
                  <div className="v2-section-line" />
                </div>

                {/* Two-column charts */}
                <div className="txd-two-col">

                  {/* LEFT: error rate chart + arc rings */}
                  <div className="txd-col">
                    <div className="txd-card">
                      <div className="txd-card-hdr">
                        <div className="txd-card-title">Error Rate — {timeFilter} · {tenantLabel}</div>
                        <div className="txd-legend">
                          <div className="txd-leg"><div className="txd-leg-line" style={{ background: '#ff4d6d' }} />5xx</div>
                          <div className="txd-leg"><div className="txd-leg-line" style={{ background: '#ffaa00' }} />4xx</div>
                        </div>
                      </div>
                      <LineChart series={errChartSeries} labels={HOURS} height={240} chartId="err-all" />
                    </div>
                    <div className="txd-arc-row">
                      <div className="txd-arc-card">
                        <ArcRing pct={kpis.errTotal > 0 ? (kpis.err4xx / kpis.errTotal) * 100 : 0}
                          color="#ffaa00" dimColor="rgba(255,170,0,.18)" size={110}
                          label="4xx Client" sublabel={`${kpis.err4xx} errors`} />
                      </div>
                      <div className="txd-arc-card">
                        <ArcRing pct={kpis.errTotal > 0 ? (kpis.err5xx / kpis.errTotal) * 100 : 0}
                          color="#ff4d6d" dimColor="rgba(255,77,109,.18)" size={110}
                          label="5xx Server" sublabel={`${kpis.err5xx} errors`} />
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: latency chart + G/A/R arc rings */}
                  <div className="txd-col">
                    <div className="txd-card">
                      <div className="txd-card-hdr">
                        <div className="txd-card-title">Latency Buckets — {timeFilter} · {tenantLabel}</div>
                        <div className="txd-legend">
                          <div className="txd-leg"><div className="txd-leg-dot" style={{ background: '#ff4d6d' }} />Red</div>
                          <div className="txd-leg"><div className="txd-leg-dot" style={{ background: '#ffaa00' }} />Amber</div>
                        </div>
                      </div>
                      <LineChart series={latChartSeries} labels={HOURS} height={240} chartId="lat-all" />
                    </div>
                    <div className="txd-arc-row">
                      <div className="txd-arc-card">
                        <ArcRing pct={kpis.total > 0 ? (kpis.amber / kpis.total) * 100 : 0}
                          color="#ffaa00" dimColor="rgba(255,170,0,.18)" size={110}
                          label="Amber (5–10s)" sublabel={`${kpis.amber}`} />
                      </div>
                      <div className="txd-arc-card">
                        <ArcRing pct={kpis.total > 0 ? (kpis.red / kpis.total) * 100 : 0}
                          color="#ff4d6d" dimColor="rgba(255,77,109,.18)" size={110}
                          label="Red (>10s)" sublabel={`${kpis.red}`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Avg duration chart — single tenant only */}
                {selectedTenant !== '__all__' && (
                  <div className="txd-card">
                    <div className="txd-card-hdr">
                      <div className="txd-card-title">Avg Response Time — {selectedTenant} · last 24h</div>
                      <div className="txd-legend">
                        <div className="txd-leg"><div className="txd-leg-line" style={{ background: '#3d8fff' }} />Avg ms</div>
                      </div>
                    </div>
                    <LineChart series={avgDurSeries} labels={HOURS} height={180} chartId="dur-tenant" />
                  </div>
                )}

                {/* ── Health Matrix / Components ── */}
                <div className="v2-section-divider">
                  <span className="v2-section-label">{selectedTenant === '__all__' ? 'Health Matrix' : 'Components'}</span>
                  <div className="v2-section-line" />
                </div>

                {/* Tenant × Component heatmap (all-tenants) OR component table (single tenant) */}
                {selectedTenant === '__all__' ? (
                  <div className="txd-card">
                    <div className="txd-card-hdr">
                      <div className="txd-card-title">Tenant × Component Health Matrix</div>
                      <div className="txd-heatmap-legend">
                        <span className="txd-hm-leg txd-hm-leg--crit">Critical (errors or &gt;10s)</span>
                        <span className="txd-hm-leg txd-hm-leg--warn">Degraded (5–10s)</span>
                        <span className="txd-hm-leg txd-hm-leg--ok">Healthy</span>
                        <span className="txd-hm-leg txd-hm-leg--none">No data</span>
                      </div>
                    </div>
                    {(tenantHeatmap.overviewTenants?.length ?? 0) === 0
                      ? <div className="txd-empty-row">No data for this time window.</div>
                      : (
                        <div className="txd-heatmap-wrap">
                          <div className="txd-heatmap" style={{ '--hm-cols': tenantHeatmap.compNames?.length ?? 1 }}>
                            {/* header row */}
                            <div className="txd-hm-corner" />
                            {(tenantHeatmap.compNames ?? []).map(c => (
                              <div key={c} className="txd-hm-col-hdr" title={c}>
                                {c.length > 14 ? c.slice(0, 12) + '…' : c}
                              </div>
                            ))}
                            {/* data rows */}
                            {(tenantHeatmap.overviewTenants ?? []).map(t => (
                              <React.Fragment key={t.tenantId}>
                                <div className="txd-hm-row-lbl" title={t.tenantId}>{t.tenantId}</div>
                                {(tenantHeatmap.compNames ?? []).map(c => {
                                  const comp = t.components.find(x => x.componentName === c);
                                  if (!comp) return (
                                    <div key={c} className="txd-hm-cell txd-hm-cell--none">—</div>
                                  );
                                  const st = (comp.errorCount > 0 || comp.redCount > 0) ? 'crit' : comp.amberCount > 0 ? 'warn' : 'ok';
                                  return (
                                    <div key={c} className={`txd-hm-cell txd-hm-cell--${st}`}
                                      onClick={() => { setSelectedTenant(t.tenantId); setView('all'); setSelectedComponent(''); }}
                                      data-tip={`${t.tenantId} / ${c} · ${comp.errorCount}e ${comp.redCount}R ${comp.amberCount}A`}>
                                      {st === 'ok' ? (
                                        <span className="txd-hm-check">✓</span>
                                      ) : (
                                        <>
                                          <div className="txd-hm-cell-err">{comp.errorCount}e</div>
                                          {(comp.redCount > 0 || comp.amberCount > 0) && (
                                            <div className="txd-hm-cell-lat">
                                              {comp.redCount > 0 && <span className="txd-hm-r">{comp.redCount}R</span>}
                                              {comp.amberCount > 0 && <span className="txd-hm-a">{comp.amberCount}A</span>}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )
                    }
                  </div>
                ) : (
                  <div className="txd-card">
                    <div className="txd-card-hdr">
                      <div className="txd-card-title">Components — click to drilldown</div>
                      <div className="txd-badge">{compSummary.length} components</div>
                    </div>
                    {compSummary.length === 0
                      ? <div className="txd-empty-row">No data for this tenant / time window.</div>
                      : (
                        <div className="txd-tbl-wrap">
                          <table className="txd-comp-table resizable-table" style={{ width: compTable.tableWidth }}>
                            <colgroup>
                              {COMP_TABLE_COLUMNS.map((c) => (
                                <col key={c.key} style={{ width: `${compTable.colWidths[c.key]}px` }} />
                              ))}
                            </colgroup>
                            <thead>
                              <tr>
                                {COMP_TABLE_COLUMNS.map((c) => (
                                  <th key={c.key} className="resizable-th">
                                    {c.label}
                                    <span className="col-resize-handle" onMouseDown={compTable.handleResizeStart(c.key)} />
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {compSummary.map(c => (
                                <tr key={c.name} onClick={() => handleSelectComponent(c.name)}>
                                  <td>
                                    <div className="txd-comp-name-cell">
                                      <div className="txd-comp-dot" style={{ background: c.color, boxShadow: `0 0 5px ${c.color}88` }} />
                                      <span className="txd-comp-name">{c.name}</span>
                                    </div>
                                  </td>
                                  <td style={{ color: c.color, fontWeight: 700 }}>{c.errRate}%</td>
                                  <td>
                                    <div className="txd-comp-bar-bg">
                                      <div className="txd-comp-bar-fill"
                                        style={{ width: `${Math.min(100, parseFloat(c.errRate) * 8)}%`, background: c.color }} />
                                    </div>
                                  </td>
                                  <td style={{ color: 'var(--txd-t2)' }}>{c.err4xx}</td>
                                  <td style={{ color: 'var(--txd-t2)' }}>{c.err5xx}</td>
                                  <td style={{ color: 'var(--txd-t2)' }}>{c.total.toLocaleString()}</td>
                                  <td style={{ color: c.red > 0 ? '#ff4d6d' : 'var(--txd-t2)' }}>{c.red}</td>
                                  <td style={{ color: c.amber > 0 ? '#ffaa00' : 'var(--txd-t2)' }}>{c.amber}</td>
                                  <td style={{ fontFamily: 'monospace', color: 'var(--txd-t2)' }}>{c.p95 ? `${c.p95}ms` : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    }
                  </div>
                )}
              </>
            )}

            {/* ── SINGLE COMPONENT VIEW ──────────────────────────────── */}
            {view === 'comp' && (
              <>
                {/* ── Component Drilldown ── */}
                <div className="v2-section-divider">
                  <span className="v2-section-label">Component Drilldown</span>
                  <div className="v2-section-line" />
                </div>

                {/* Breadcrumb */}
                <div className="txd-breadcrumb">
                  <span className="txd-breadcrumb-link" onClick={handleBackToAll}>All Components</span>
                  <span className="txd-breadcrumb-sep">/</span>
                  <span className="txd-breadcrumb-cur">{selectedComponent}</span>
                </div>

                {/* KPI strip */}
                <div className="txd-kpi-row">
                  <KpiCard label="Error Rate"   value={`${kpis.errRate}%`}  sub="4xx + 5xx"              cls="red" />
                  <KpiCard label="Errors Today" value={kpis.errTotal}        sub="this component"          cls="red" />
                  <KpiCard label="Slow Calls"   value={`${kpis.slowPct}%`}  sub="Red + Amber"             cls="amb" />
                  <KpiCard label="Total Calls"  value={kpis.total.toLocaleString()} sub="this component"   cls="cq" />
                  <KpiCard label="Avg Latency"  value={kpis.avgDur ? `${kpis.avgDur}ms` : '—'} sub="mean duration" cls="grn" />
                </div>

                {/* Error chart + arc rings */}
                <div className="txd-two-col-6040">
                  <div className="txd-card">
                    <div className="txd-card-hdr">
                      <div className="txd-card-title">Error Rate — {selectedComponent} · {timeFilter}</div>
                      <div className="txd-legend">
                        <div className="txd-leg"><div className="txd-leg-line" style={{ background: '#ff4d6d' }} />5xx</div>
                        <div className="txd-leg"><div className="txd-leg-line" style={{ background: '#ffaa00' }} />4xx</div>
                      </div>
                    </div>
                    <LineChart series={errChartSeries} labels={HOURS} height={270} chartId="err-comp" />
                  </div>
                  <div className="txd-arc-row txd-arc-row--col">
                    <div className="txd-arc-card txd-arc-card--tall">
                      <ArcRing pct={kpis.errTotal > 0 ? (kpis.err5xx / kpis.errTotal) * 100 : 0}
                        color="#ff4d6d" dimColor="rgba(255,77,109,.18)" size={130}
                        label="5xx Server" sublabel={`${kpis.err5xx} errors`} />
                    </div>
                    <div className="txd-arc-card txd-arc-card--tall">
                      <ArcRing pct={kpis.errTotal > 0 ? (kpis.err4xx / kpis.errTotal) * 100 : 0}
                        color="#ffaa00" dimColor="rgba(255,170,0,.18)" size={130}
                        label="4xx Client" sublabel={`${kpis.err4xx} errors`} />
                    </div>
                  </div>
                </div>

                {/* Latency chart */}
                <div className="txd-card">
                  <div className="txd-card-hdr">
                    <div className="txd-card-title">Latency Buckets — {selectedComponent} · {timeFilter}</div>
                    <div className="txd-legend">
                      <div className="txd-leg"><div className="txd-leg-dot" style={{ background: '#ff4d6d' }} />Red</div>
                      <div className="txd-leg"><div className="txd-leg-dot" style={{ background: '#ffaa00' }} />Amber</div>
                      <div className="txd-leg"><div className="txd-leg-dot" style={{ background: '#00cc76' }} />Green</div>
                    </div>
                  </div>
                  <LineChart series={latChartSeries} labels={HOURS} height={280} chartId="lat-comp" />
                </div>

                {/* Drilldown table */}
                <div className="txd-section-row">
                  <span className="txd-section-lbl">Drilldown</span>
                  <div className="txd-section-line" />
                </div>

                <div className="txd-tabs">
                  <button className={`txd-tab${activeTab === 'errors' ? ' txd-tab--active' : ''}`}
                    onClick={() => setActiveTab('errors')}>
                    Errors <span className="txd-tab-badge">{drillLoading ? '…' : errDrillRows.length}</span>
                  </button>
                  <button className={`txd-tab${activeTab === 'latency' ? ' txd-tab--active' : ''}`}
                    onClick={() => setActiveTab('latency')}>
                    Latency <span className="txd-tab-badge txd-tab-badge--amb">{drillLoading ? '…' : latDrillRows.length}</span>
                  </button>
                </div>

                <div style={{ display: activeTab === 'errors' ? 'block' : 'none' }}>
                  <div className="txd-tbl-wrap">
                    <div className="txd-tbl-toolbar">
                      <span className="txd-tbl-count"><strong>{errDrillRows.length}</strong> errors · {selectedComponent}</span>
                    </div>
                    <table className="txd-dt resizable-table" style={{ width: errDrillTable.tableWidth }}>
                      <colgroup>
                        {ERROR_DRILL_COLUMNS.map((c) => (
                          <col key={c.key} style={{ width: `${errDrillTable.colWidths[c.key]}px` }} />
                        ))}
                      </colgroup>
                      <thead>
                        <tr>
                          {ERROR_DRILL_COLUMNS.map((c) => (
                            <th key={c.key} className="resizable-th">
                              {c.label}
                              <span className="col-resize-handle" onMouseDown={errDrillTable.handleResizeStart(c.key)} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {errDrillRows.length === 0 && !drillLoading && (
                          <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--txd-t3)', padding: '20px' }}>No errors found for today.</td></tr>
                        )}
                        {errDrillRows.map((r, i) => (
                          <tr key={i}>
                            <td className="txd-ts">{r.timestamp ? new Date(r.timestamp).toLocaleTimeString('en-ZA') : '—'}</td>
                            <td><span className="txd-method" style={{ color: MC[r.httpMethod] || '#aaa' }}>{r.httpMethod}</span></td>
                            <td className="txd-ep">{r.endpoint}</td>
                            <td><span className={`txd-badge ${statusClass(r.statusCode)}`}>{r.statusCode}</span></td>
                            <td className={latClass(r.latBucket)}>{r.durationMs != null ? `${r.durationMs}ms` : '—'}</td>
                            <td><span className={latBadge(r.latBucket)}>{r.latBucket || '—'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={{ display: activeTab === 'latency' ? 'block' : 'none' }}>
                  <div className="txd-tbl-wrap">
                    <div className="txd-tbl-toolbar">
                      <span className="txd-tbl-count"><strong>{latDrillRows.length}</strong> slow calls · {selectedComponent}</span>
                    </div>
                    <table className="txd-dt resizable-table" style={{ width: latDrillTable.tableWidth }}>
                      <colgroup>
                        {LATENCY_DRILL_COLUMNS.map((c) => (
                          <col key={c.key} style={{ width: `${latDrillTable.colWidths[c.key]}px` }} />
                        ))}
                      </colgroup>
                      <thead>
                        <tr>
                          {LATENCY_DRILL_COLUMNS.map((c) => (
                            <th key={c.key} className="resizable-th">
                              {c.label}
                              <span className="col-resize-handle" onMouseDown={latDrillTable.handleResizeStart(c.key)} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {latDrillRows.length === 0 && !drillLoading && (
                          <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--txd-t3)', padding: '20px' }}>No slow calls found for today.</td></tr>
                        )}
                        {latDrillRows.map((r, i) => (
                          <tr key={i}>
                            <td className="txd-ts">{r.timestamp ? new Date(r.timestamp).toLocaleTimeString('en-ZA') : '—'}</td>
                            <td><span className="txd-method" style={{ color: MC[r.httpMethod] || '#aaa' }}>{r.httpMethod}</span></td>
                            <td className="txd-ep">{r.endpoint}</td>
                            <td><span className={`txd-badge ${statusClass(r.statusCode)}`}>{r.statusCode}</span></td>
                            <td className={latClass(r.latBucket)}>{r.durationMs != null ? `${r.durationMs}ms` : '—'}</td>
                            <td><span className={latBadge(r.latBucket)}>{r.latBucket || '—'}</span></td>
                            <td>{r.error === 'Y'
                              ? <span className="txd-badge b5">Y</span>
                              : <span style={{ color: 'var(--txd-t3)', fontSize: '.7rem' }}>N</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
